import { api } from './apiClient';
import type {
  Candidate,
  InterviewConfig,
  ATSConfig,
  Plan,
  DashboardStats,
} from '@/store/slices/employerSlice';

interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

export const employerService = {
  // Dashboard
  async getDashboardStats(): Promise<DashboardStats> {
    return api.interviewAgent.get('/employer/dashboard/stats');
  },

  // Candidates
  async getCandidates(params: {
    page?: number;
    limit?: number;
    status?: string;
  }): Promise<{ candidates: Candidate[]; total: number }> {
    const response = await api.interviewAgent.get<PaginatedResponse<Candidate>>(
      '/employer/candidates',
      {
        params,
      }
    );
    return {
      candidates: response.data,
      total: response.total,
    };
  },

  async getCandidate(uid: string): Promise<Candidate> {
    return api.interviewAgent.get(`/employer/candidates/${uid}`);
  },

  async registerCandidate(candidate: {
    name: string;
    email: string;
    phone?: string;
    resumeUrl?: string;
  }): Promise<Candidate> {
    return api.interviewAgent.post('/employer/candidates', candidate);
  },

  async updateCandidate(
    uid: string,
    data: Partial<Candidate>
  ): Promise<Candidate> {
    return api.interviewAgent.put(`/employer/candidates/${uid}`, data);
  },

  async deleteCandidate(uid: string): Promise<void> {
    return api.interviewAgent.delete(`/employer/candidates/${uid}`);
  },

  // Interview Configuration
  async getInterviewConfigs(): Promise<InterviewConfig[]> {
    return api.interviewAgent.get('/employer/interview-configs');
  },

  async getInterviewConfig(uid: string): Promise<InterviewConfig> {
    return api.interviewAgent.get(`/employer/interview-configs/${uid}`);
  },

  async createInterviewConfig(
    config: Omit<InterviewConfig, 'uid'>
  ): Promise<InterviewConfig> {
    return api.interviewAgent.post('/employer/interview-configs', config);
  },

  async updateInterviewConfig(
    config: InterviewConfig
  ): Promise<InterviewConfig> {
    return api.interviewAgent.put(
      `/employer/interview-configs/${config.uid}`,
      config
    );
  },

  async deleteInterviewConfig(uid: string): Promise<void> {
    return api.interviewAgent.delete(`/employer/interview-configs/${uid}`);
  },

  // ATS Configuration
  async getATSConfig(): Promise<ATSConfig | null> {
    try {
      return await api.interviewAgent.get('/employer/ats-config');
    } catch {
      return null;
    }
  },

  async updateATSConfig(config: Partial<ATSConfig>): Promise<ATSConfig> {
    return api.interviewAgent.put('/employer/ats-config', config);
  },

  // Plans
  async getCurrentPlan(): Promise<Plan> {
    return api.interviewAgent.get('/employer/plan');
  },

  // API Keys
  async getApiKeys(): Promise<
    { uid: string; key: string; name: string; createdAt: string }[]
  > {
    return api.interviewAgent.get('/employer/api-keys');
  },

  async generateApiKey(
    name: string
  ): Promise<{ uid: string; key: string; name: string; createdAt: string }> {
    return api.interviewAgent.post('/employer/api-keys', { name });
  },

  async deleteApiKey(uid: string): Promise<void> {
    return api.interviewAgent.delete(`/employer/api-keys/${uid}`);
  },

  // Generate interview link
  async generateInterviewLink(candidateUid: string): Promise<{ link: string }> {
    return api.interviewAgent.post('/employer/generate-link', { candidateUid });
  },
};
